import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Check, ChevronRight, ChevronLeft, Loader2, PartyPopper, MapPin, CalendarDays, User, Sparkles, ShieldCheck, Star, Plus, Trash2, Users } from "lucide-react";

const TIMES = ["06:00 AM", "08:00 AM", "10:00 AM", "12:00 PM", "04:00 PM", "06:00 PM", "07:30 PM"];
const STEP_LABELS = ["Selection", "Date & Time", "Purohit", "Devotees", "Plan", "Add-ons", "Summary"];
const GENDERS = ["Male", "Female", "Other"];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function BookingFlow() {
  const { type } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [services, setServices] = useState([]);
  const [purohits, setPurohits] = useState([]);
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [bp, setBp] = useState({ devotee_pct: 15, max_devotees: 9, plan_standard: 0, plan_premium: 1100, group_discount: 0, group_min: 4 });
  const [family, setFamily] = useState([]);
  const [availableTimes, setAvailableTimes] = useState(null);

  const [step, setStep] = useState(0);
  const entryMandir = params.get("mandir") || "";
  const [serviceId, setServiceId] = useState(params.get("service") || "");
  const [mandirId, setMandirId] = useState(entryMandir);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [purohitId, setPurohitId] = useState("");
  const [devotees, setDevotees] = useState([{ name: "", age: "", gender: "" }]);
  const [planId, setPlanId] = useState("standard");
  const [addonIds, setAddonIds] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    api.get("/services", { params: { type } }).then((r) => setServices(r.data));
    api.get("/plans").then((r) => setPlans(r.data));
    api.get("/addons").then((r) => setAddons(r.data));
    api.get("/booking-pricing").then((r) => setBp(r.data)).catch(() => {});
  }, [type]);

  useEffect(() => {
    if (user) {
      setDevotees((d) => (d.length === 1 && !d[0].name ? [{ ...d[0], name: user.name || "" }] : d));
      api.get("/me/family").then((r) => setFamily(r.data || [])).catch(() => {});
    }
  }, [user]);

  const service = useMemo(() => services.find((s) => s.id === serviceId), [services, serviceId]);
  const serviceOptions = useMemo(
    () => (entryMandir ? services.filter((s) => (s.mandirs || []).some((m) => m.id === entryMandir)) : services),
    [services, entryMandir]
  );
  const mandirOptions = useMemo(() => service?.mandirs || [], [service]);
  const selectedMandir = useMemo(() => mandirOptions.find((m) => m.id === mandirId), [mandirOptions, mandirId]);

  // Fetch available time slots when mandir + date are set
  useEffect(() => {
    if (mandirId && date) {
      setAvailableTimes(null);
      api.get("/available-times", { params: { mandir_id: mandirId, service_type: type, date } })
        .then((r) => {
          setAvailableTimes(r.data || []);
          setTime((prev) => ((r.data || []).includes(prev) ? prev : ""));
        })
        .catch((e) => {
          const msg = e?.response?.data?.detail || "Could not load available time slots — please try again.";
          toast.error(msg);
          setAvailableTimes([]);
        });
    } else {
      setAvailableTimes(null);
    }
  }, [mandirId, date, type]);

  // Fetch available purohits when reaching purohit step
  useEffect(() => {
    if (step === 2 && mandirId && date && time) {
      api.get("/purohits", { params: { mandir_id: mandirId, service_type: type, date, time } })
        .then((r) => {
          setPurohits(r.data);
          setPurohitId((prev) => (r.data.some((p) => p.id === prev) ? prev : r.data[0]?.id || ""));
        })
        .catch((e) => {
          const msg = e?.response?.data?.detail || "Could not load purohits — please try again.";
          toast.error(msg);
          // Keep prior purohits list intact so the user isn't stranded.
        });
    }
  }, [step, mandirId, date, time, type]);

  // If the chosen service doesn't offer the current mandir, clear mandir
  useEffect(() => {
    if (service && mandirId && !mandirOptions.some((m) => m.id === mandirId)) setMandirId("");
  }, [service]); // eslint-disable-line

  const nDev = Math.min(devotees.length, bp.max_devotees);
  const plan = plans.find((p) => p.id === planId);
  const pricing = useMemo(() => {
    if (!service) return null;
    const mult = 1 + (Number(bp.devotee_pct) / 100) * (nDev - 1);
    const base = Math.round(service.price * mult);
    const items = [{ label: service.name + (nDev > 1 ? ` (×${nDev} devotees)` : ""), amount: base }];
    const planAmt = Math.round((plan?.price_add || 0) * mult);
    if (planAmt) items.push({ label: `${plan.name} plan`, amount: planAmt });
    addons.filter((a) => addonIds.includes(a.id)).forEach((a) => items.push({ label: a.name, amount: a.price }));
    const gd = Number(bp.group_discount) || 0;
    const gm = Math.max(2, Number(bp.group_min) || 4);
    if (gd > 0 && nDev >= gm) items.push({ label: `Group discount (×${nDev} devotees)`, amount: -(gd * nDev) });
    const subtotal = items.reduce((s, i) => s + i.amount, 0);
    const tax = Math.round(subtotal * 0.05);
    return { items, subtotal, tax, total: subtotal + tax };
  }, [service, plan, addons, addonIds, nDev, bp]);

  const toggleAddon = (id) => setAddonIds((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));
  const setDev = (i, patch) => setDevotees((d) => d.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const addDev = () => setDevotees((d) => (d.length < bp.max_devotees ? [...d, { name: "", age: "", gender: "" }] : d));
  const removeDev = (i) => setDevotees((d) => (d.length > 1 ? d.filter((_, idx) => idx !== i) : d));
  const addFromFamily = (m) => setDevotees((d) => {
    if (d.some((x) => x.name.trim().toLowerCase() === String(m.name).toLowerCase())) return d;
    const row = { name: m.name, age: m.age != null ? String(m.age) : "", gender: m.gender || "" };
    const emptyIdx = d.findIndex((x) => !x.name.trim() && !String(x.age).trim() && !x.gender);
    if (emptyIdx >= 0) return d.map((x, idx) => (idx === emptyIdx ? row : x));
    return d.length < bp.max_devotees ? [...d, row] : d;
  });
  const availableFamily = family.filter((m) => !devotees.some((x) => x.name.trim().toLowerCase() === String(m.name).toLowerCase()));

  const devoteesValid = devotees.every((d) => d.name.trim() && String(d.age).trim() && d.gender);
  const canNext = () => {
    if (step === 0) return !!serviceId && !!mandirId;
    if (step === 1) return !!date && !!time;
    if (step === 2) return !!purohitId;
    if (step === 3) return devoteesValid;
    return true;
  };

  const handlePay = async () => {
    setProcessing(true);
    try {
      const bookingRes = await api.post("/bookings", {
        booking_type: type, service_id: serviceId, mandir_id: mandirId, purohit_id: purohitId,
        date, time, plan_id: planId, addon_ids: addonIds,
        devotees: devotees.map((d) => ({ name: d.name, age: Number(d.age) || null, gender: d.gender })),
      });
      const booking = bookingRes.data;
      const orderRes = await api.post("/payments/create-order", { booking_id: booking.id });
      const order = orderRes.data;
      if (order.live) {
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Could not load payment gateway"); setProcessing(false); return; }
        const rzp = new window.Razorpay({
          key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
          name: "Pavitra Mantra", description: service.name,
          prefill: { name: devotees[0]?.name, email: user?.email },
          theme: { color: "#E67E22" },
          handler: async (resp) => {
            const v = await api.post("/payments/verify", {
              booking_id: booking.id, razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_order_id: resp.razorpay_order_id, razorpay_signature: resp.razorpay_signature,
            });
            setConfirmed(v.data); toast.success("Booking confirmed!");
          },
          modal: { ondismiss: () => setProcessing(false) },
        });
        rzp.open();
      } else {
        const v = await api.post("/payments/verify", { booking_id: booking.id });
        setConfirmed(v.data);
        toast.success("Booking confirmed (test payment)");
        setProcessing(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed. Please try again.");
      setProcessing(false);
    }
  };

  if (confirmed) return <Confirmation booking={confirmed} navigate={navigate} />;

  return (
    <div className="px-4 sm:px-6 lg:px-12 max-w-5xl mx-auto py-10 lg:py-14" data-testid="booking-stepper-container">
      <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-2">{type === "ritual" ? "Book Vedic Ritual" : "Book Worship"}</p>
      <h1 className="font-serif text-3xl lg:text-4xl font-bold mb-8">Complete your sacred booking</h1>

      <div className="flex items-center gap-1 mb-10 overflow-x-auto pb-2">
        {STEP_LABELS.map((l, i) => (
          <React.Fragment key={l}>
            <div className={`flex items-center gap-2 shrink-0 ${i <= step ? "text-primary" : "text-muted-foreground"}`}>
              <span className={`h-8 w-8 grid place-items-center rounded-full text-sm font-semibold border-2 ${i < step ? "bg-primary text-primary-foreground border-primary" : i === step ? "border-primary" : "border-border"}`}>
                {i < step ? <Check className="h-4 w-4" /> : i + 1}
              </span>
              <span className="text-sm font-medium hidden sm:block">{l}</span>
            </div>
            {i < STEP_LABELS.length - 1 && <div className={`h-0.5 flex-1 min-w-[12px] ${i < step ? "bg-primary" : "bg-border"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Step 0: Selection — service + mandir (works both entry paths) */}
          {step === 0 && (
            <div data-testid="step-selection" className="space-y-6">
              <div>
                <h2 className="font-serif text-xl font-semibold mb-1">Choose your {type === "ritual" ? "Vedic ritual" : "worship"}</h2>
                {entryMandir && selectedMandir && <p className="text-sm text-muted-foreground mb-3">Offered at <span className="text-primary font-medium">{selectedMandir.name}</span></p>}
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  {serviceOptions.map((s) => (
                    <button key={s.id} onClick={() => { setServiceId(s.id); if (!entryMandir) setMandirId(""); }} data-testid={`select-service-${s.id}`}
                      className={`text-left rounded-xl border p-4 flex gap-3 transition-colors ${serviceId === s.id ? "border-primary bg-primary/5" : "border-border hover:border-accent"}`}>
                      <img src={s.image} alt="" className="h-16 w-16 rounded-lg object-cover" />
                      <div className="min-w-0">
                        <p className="font-serif font-semibold leading-tight">{s.name}</p>
                        <p className="text-xs text-accent">{s.deity}</p>
                        <p className="text-sm font-semibold text-primary mt-1">From {inr(s.price)}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {service && (
                <div data-testid="mandir-picker">
                  <h2 className="font-serif text-xl font-semibold mb-1">Select the Mandir where it will be performed</h2>
                  <p className="text-sm text-muted-foreground mb-3">{service.name} is offered at these Mandirs.</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {mandirOptions.map((m) => (
                      <button key={m.id} onClick={() => setMandirId(m.id)} data-testid={`select-mandir-${m.id}`}
                        className={`text-left rounded-xl border p-4 flex gap-3 transition-colors ${mandirId === m.id ? "border-primary bg-primary/5" : "border-border hover:border-accent"}`}>
                        <img src={m.image} alt="" className="h-16 w-16 rounded-lg object-cover" />
                        <div className="min-w-0">
                          <p className="font-serif font-semibold leading-tight">{m.name}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3 text-accent" /> {m.city}, {m.state}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 1: Date & time */}
          {step === 1 && (
            <div data-testid="step-datetime" className="space-y-5">
              <h2 className="font-serif text-xl font-semibold">Choose date & time</h2>
              <div>
                <Label className="mb-2 block">Date</Label>
                <Input type="date" data-testid="booking-date" value={date} min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => { setDate(e.target.value); setTime(""); }} className="rounded-xl max-w-xs" />
              </div>
              <div>
                <Label className="mb-2 block">Time slot</Label>
                {!date ? (
                  <p className="text-sm text-muted-foreground">Please pick a date first.</p>
                ) : availableTimes === null ? (
                  <p className="text-sm text-muted-foreground" data-testid="times-loading">Checking Purohit availability…</p>
                ) : availableTimes.length === 0 ? (
                  <p className="text-sm text-amber-600" data-testid="no-times">No Purohits are available on this date. Please pick another date.</p>
                ) : (
                  <div className="flex flex-wrap gap-2" data-testid="time-slots">
                    {TIMES.map((t) => {
                      const open = availableTimes.includes(t);
                      if (!open) return null;
                      return (
                        <button key={t} onClick={() => setTime(t)} data-testid={`time-${t.replace(/[:\s]/g, "")}`}
                          className={`px-4 py-2 rounded-full text-sm border transition-colors ${time === t ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-accent"}`}>{t}</button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Purohit — available at this mandir on this date/time */}
          {step === 2 && (
            <div data-testid="step-purohit" className="space-y-4">
              <h2 className="font-serif text-xl font-semibold">Choose your Purohit</h2>
              <p className="text-sm text-muted-foreground">Verified purohits available at {selectedMandir?.name || "this Mandir"} on {date} at {time}.</p>
              {purohits.length === 0 && <p className="text-amber-600 text-sm" data-testid="no-purohits">No purohit is available for this slot. Please go back and pick another date or time.</p>}
              <div className="space-y-3">
                {purohits.map((p) => (
                  <button key={p.id} onClick={() => setPurohitId(p.id)} data-testid={`select-purohit-${p.id}`}
                    className={`w-full text-left rounded-xl border p-4 flex gap-4 items-center transition-colors ${purohitId === p.id ? "border-primary bg-primary/5" : "border-border hover:border-accent"}`}>
                    <img src={p.image} alt="" className="h-14 w-14 rounded-full object-cover" />
                    <div className="min-w-0 flex-1">
                      <p className="font-serif font-semibold flex items-center gap-1.5">{p.name} <ShieldCheck className="h-4 w-4 text-emerald-500" /></p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                        {p.rating_count > 0 && <span className="flex items-center gap-0.5 text-accent"><Star className="h-3 w-3 fill-accent" /> {p.rating} ({p.rating_count})</span>}
                        <span>{p.experience} yrs experience · {p.certifications}</span>
                      </p>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{p.bio}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Devotees */}
          {step === 3 && (
            <div data-testid="step-devotee" className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-xl font-semibold flex items-center gap-2"><Users className="h-5 w-5 text-accent" /> Devotees attending</h2>
                <span className="text-xs text-muted-foreground">Up to {bp.max_devotees}</span>
              </div>
              <p className="text-sm text-muted-foreground">Add everyone attending the {type}. The price adjusts with the number of devotees.</p>
              {availableFamily.length > 0 && (
                <div className="rounded-xl border border-accent/40 bg-accent/5 p-4" data-testid="family-prefill">
                  <p className="text-xs font-semibold text-accent uppercase tracking-wide flex items-center gap-1.5 mb-2"><Users className="h-3.5 w-3.5" /> Your saved family — tap to add</p>
                  <div className="flex flex-wrap gap-2">
                    {availableFamily.map((m) => (
                      <button key={m.name} type="button" data-testid={`family-add-${m.name}`} onClick={() => addFromFamily(m)} disabled={devotees.length >= bp.max_devotees}
                        className="inline-flex items-center gap-1.5 rounded-full border border-accent/50 bg-card px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-40">
                        <Plus className="h-3.5 w-3.5" /> {m.name}{m.age ? ` (${m.age})` : ""}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-3">
                {devotees.map((d, i) => (
                  <div key={i} data-testid={`devotee-row-${i}`} className="rounded-xl border border-border p-4 grid sm:grid-cols-[1fr_100px_130px_auto] gap-3 items-end">
                    <div><Label className="mb-1.5 block text-xs">Full Name *</Label><Input data-testid={`devotee-name-${i}`} value={d.name} onChange={(e) => setDev(i, { name: e.target.value })} className="rounded-xl" /></div>
                    <div><Label className="mb-1.5 block text-xs">Age *</Label><Input type="number" min="0" data-testid={`devotee-age-${i}`} value={d.age} onChange={(e) => setDev(i, { age: e.target.value })} className="rounded-xl" /></div>
                    <div>
                      <Label className="mb-1.5 block text-xs">Gender *</Label>
                      <select data-testid={`devotee-gender-${i}`} value={d.gender} onChange={(e) => setDev(i, { gender: e.target.value })} className="w-full h-10 rounded-xl border border-border bg-background px-3 text-sm">
                        <option value="">Select</option>
                        {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                    <Button variant="ghost" size="icon" data-testid={`devotee-remove-${i}`} disabled={devotees.length === 1} onClick={() => removeDev(i)} className="rounded-full text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" data-testid="add-devotee-btn" disabled={devotees.length >= bp.max_devotees} onClick={addDev} className="rounded-full border-accent"><Plus className="h-4 w-4 mr-1.5" /> Add another devotee</Button>
            </div>
          )}

          {/* Step 4: Plan */}
          {step === 4 && (
            <div data-testid="step-plan" className="space-y-4">
              <h2 className="font-serif text-xl font-semibold">Choose a plan</h2>
              <RadioGroup value={planId} onValueChange={setPlanId} className="grid sm:grid-cols-2 gap-4">
                {plans.map((p) => {
                  const mult = 1 + (Number(bp.devotee_pct) / 100) * (nDev - 1);
                  const amt = Math.round((p.price_add || 0) * mult);
                  return (
                  <label key={p.id} data-testid={`plan-${p.id}`} className={`relative rounded-xl border p-5 cursor-pointer transition-colors ${planId === p.id ? "border-primary bg-primary/5" : "border-border hover:border-accent"}`}>
                    {(p.popular_tag || p.discount_tag) && (
                      <div className="absolute -top-2.5 right-3 flex gap-1.5">
                        {p.popular_tag && <span data-testid={`plan-popular-${p.id}`} className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-primary text-primary-foreground shadow-sm">{p.popular_tag}</span>}
                        {p.discount_tag && <span data-testid={`plan-discount-${p.id}`} className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-emerald-600 text-white shadow-sm">{p.discount_tag}</span>}
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-serif text-lg font-semibold">{p.name}</span>
                      <RadioGroupItem value={p.id} />
                    </div>
                    <p className="text-sm font-semibold text-primary mb-2">{amt ? `+ ${inr(amt)}` : "Included"}</p>
                    <p className="text-sm text-muted-foreground mb-3">{p.description}</p>
                    {(p.features || []).length > 0 && (
                      <ul className="space-y-1.5 mb-3">{p.features.map((f) => <li key={f} className="flex gap-2 text-sm"><Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> {f}</li>)}</ul>
                    )}
                    {(p.samagri || []).length > 0 && (
                      <div className="pt-3 border-t border-border/60 space-y-1.5">
                        <p className="text-[11px] uppercase tracking-wide text-accent font-semibold">Puja samagri &amp; details</p>
                        {p.samagri.map((it, idx) => (
                          <div key={`${p.id}-item-${idx}`} data-testid={`plan-item-${p.id}-${idx}`} className="text-xs flex flex-wrap items-baseline gap-x-2">
                            <span className="text-muted-foreground">{it.label}:</span>
                            <span className="font-medium">{it.value}</span>
                            {(it.options || []).length > 0 && (
                              <span className="text-muted-foreground/80">— {it.options.join(", ")}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </label>
                  );
                })}
              </RadioGroup>
            </div>
          )}

          {/* Step 5: Add-ons */}
          {step === 5 && (
            <div data-testid="step-addons" className="space-y-4">
              <h2 className="font-serif text-xl font-semibold">Value-added offerings</h2>
              <p className="text-sm text-muted-foreground">Enhance your puja with these optional offerings.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {addons.map((a) => (
                  <label key={a.id} data-testid={`addon-${a.id}`} className={`rounded-xl border p-4 flex items-center gap-3 cursor-pointer transition-colors ${addonIds.includes(a.id) ? "border-primary bg-primary/5" : "border-border hover:border-accent"}`}>
                    <Checkbox checked={addonIds.includes(a.id)} onCheckedChange={() => toggleAddon(a.id)} data-testid={`addon-check-${a.id}`} />
                    <div className="flex-1"><p className="font-medium text-sm">{a.name}</p></div>
                    <span className="text-sm font-semibold text-primary">{inr(a.price)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 6: Summary */}
          {step === 6 && service && (
            <div data-testid="step-summary" className="space-y-4">
              <h2 className="font-serif text-xl font-semibold">Booking summary</h2>
              <div className="rounded-xl border border-border p-5 space-y-2.5 text-sm">
                <Row icon={Sparkles} label={type === "ritual" ? "Ritual" : "Worship"} value={service.name} />
                <Row icon={MapPin} label="Mandir" value={selectedMandir?.name} />
                <Row icon={CalendarDays} label="Date & Time" value={`${date || "—"} · ${time || "—"}`} />
                <Row icon={User} label="Purohit" value={purohits.find((p) => p.id === purohitId)?.name || "—"} />
                <Row icon={Users} label="Devotees" value={devotees.map((d) => `${d.name} (${d.age}/${d.gender?.[0] || "-"})`).join(", ")} />
                <Row icon={ShieldCheck} label="Plan" value={plan?.name} />
                {addonIds.length > 0 && <Row icon={Check} label="Add-ons" value={addons.filter((a) => addonIds.includes(a.id)).map((a) => a.name).join(", ")} />}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-4">
            <Button variant="ghost" data-testid="booking-back" disabled={step === 0} onClick={() => setStep(step - 1)} className="rounded-full"><ChevronLeft className="mr-1 h-4 w-4" /> Back</Button>
            {step < 6 ? (
              <Button data-testid="booking-next" disabled={!canNext()} onClick={() => setStep(step + 1)} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-6">Continue <ChevronRight className="ml-1 h-4 w-4" /></Button>
            ) : (
              <Button data-testid="booking-pay" disabled={processing} onClick={handlePay} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11">
                {processing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing…</> : <>Pay {inr(pricing?.total)}</>}
              </Button>
            )}
          </div>
        </div>

        {/* Price summary sidebar */}
        <aside>
          <div className="rounded-2xl border border-accent/40 bg-card p-5 sticky top-24" data-testid="price-summary">
            {service && (
              <>
                <img src={service.image} alt="" className="h-32 w-full rounded-xl object-cover mb-4" />
                <h3 className="font-serif text-lg font-semibold leading-tight">{service.name}</h3>
                {selectedMandir && <p className="text-xs text-muted-foreground mb-4 flex items-center gap-1"><MapPin className="h-3 w-3 text-accent" /> {selectedMandir.name}</p>}
              </>
            )}
            {pricing && (
              <div className="space-y-2 text-sm border-t border-border pt-3">
                {pricing.items.map((i, idx) => <div key={`${i.label}-${idx}`} className="flex justify-between"><span className="text-muted-foreground">{i.label}</span><span className={i.amount < 0 ? "text-emerald-600" : ""}>{inr(i.amount)}</span></div>)}
                <div className="flex justify-between text-muted-foreground pt-2 border-t border-border"><span>Subtotal</span><span>{inr(pricing.subtotal)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>GST (5%)</span><span>{inr(pricing.tax)}</span></div>
                <div className="flex justify-between font-serif text-lg font-bold text-primary pt-2 border-t border-border"><span>Total</span><span>{inr(pricing.total)}</span></div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex justify-between gap-4">
    <span className="flex items-center gap-2 text-muted-foreground shrink-0"><Icon className="h-4 w-4 text-accent" /> {label}</span>
    <span className="font-medium text-right">{value}</span>
  </div>
);

function Confirmation({ booking, navigate }) {
  return (
    <div className="px-4 max-w-lg mx-auto py-20 text-center" data-testid="booking-confirmation">
      <span className="h-20 w-20 mx-auto grid place-items-center rounded-full bg-emerald-500/15 text-emerald-500 diya-glow mb-6"><PartyPopper className="h-10 w-10" /></span>
      <h1 className="font-serif text-3xl font-bold">Booking Confirmed!</h1>
      <p className="font-devanagari text-accent mt-2">॥ शुभम् ॥</p>
      <p className="text-muted-foreground mt-3">Your {booking.booking_type} <b>{booking.service_name}</b> at {booking.mandir_name} is confirmed for {booking.date} at {booking.time}.</p>
      <div className="rounded-2xl border border-border bg-card p-5 mt-6 text-left text-sm space-y-2">
        <div className="flex justify-between"><span className="text-muted-foreground">Booking ID</span><span className="font-mono font-semibold">{booking.id}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Purohit</span><span>{booking.purohit_name}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Amount Paid</span><span className="font-semibold text-primary">{inr(booking.total)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span className="text-emerald-500 font-medium capitalize">{booking.status}</span></div>
      </div>
      <div className="flex gap-3 justify-center mt-7">
        <Button onClick={() => navigate("/my-bookings")} data-testid="view-bookings-btn" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">View My Bookings</Button>
        <Button variant="outline" onClick={() => navigate("/divine-bless")} className="rounded-full border-accent">Book Another</Button>
      </div>
    </div>
  );
}
